import { useRef, useState } from 'react'

import './MultiSelect.scss'
import { useOnClickOutsideSelectors } from '../../Hooks/useOnClickOutsideSelectors'
import { useLocalization } from '../../Hooks/useLocalization'
import { isEmpty } from '../../Utils/Validations'

interface MultiSelectProps {
  items: Array<{ value: string; label: string }>
  values?: string[]
  onChange?: (newValue: string[]) => void
  label?: string
  helperText?: string
  isValid?: boolean
  isReadOnly?: boolean
}

export function MultiSelect({
  items,
  values = [],
  onChange,
  label,
  helperText,
  isValid,
  isReadOnly,
}: MultiSelectProps) {
  const localization = useLocalization('components')
  const [value, setValue] = useState<string>('')
  const [isShowListbox, setIsShowListbox] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleRemove = (val: string) => {
    if (onChange) {
      onChange([...values.filter((i: string) => i !== val)])
    }
  }

  const handleAddValue = (val: string) => {
    setValue('')
    if (onChange) {
      if (!values.includes(val) && !!val.trim()) {
        onChange([...values, val])
      }
    }
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const searchedList = items
    .filter((o) => !values.includes(o.value))
    .filter(
      (option) =>
        `${option.label ?? ''}`
          .toLowerCase()
          .includes(`${value ?? ''}`.toLowerCase()) &&
        !values.includes(option.label)
    )

  useOnClickOutsideSelectors(['.multi-select-wrapper'], () => {
    setIsShowListbox(false)
  })

  return (
    <div
      className={`multi-select ${isValid === false ? 'invalid' : ''} ${
        isReadOnly ?? false ? 'is-read-only' : ''
      }`}
    >
      {!isEmpty(label) ? <div className="select-label">{label}</div> : null}
      <div className="multi-select-wrapper">
        <div
          className="value-wrapper"
          onClick={() => {
            setIsShowListbox(true)
            if (inputRef.current) {
              inputRef.current.focus()
            }
          }}
        >
          {values.map((value: string) => (
            <div key={value} className="multi-select-value">
              {items.find((o) => o.value === value)?.label}
              {isReadOnly !== true && (
                <span
                  className="multi-select-closeicon"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(value)
                  }}
                >
                  🗙
                </span>
              )}
            </div>
          ))}
          <input
            ref={inputRef}
            type="search"
            className={`multi-select-input${
              isValid === false ? ' invalid' : ''
            }`}
            autoComplete="off"
            placeholder={
              values.length < 1
                ? localization.text(
                    'select-from-list',
                    'Selecteer uit de lijst'
                  )
                : ''
            }
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
            }}
          />
        </div>
        {isShowListbox && searchedList.length > 0 && (
          <ul className="list-box">
            {searchedList.map((option, index) => (
              <li
                key={option.value}
                className="list-box-item"
                onClick={() => {
                  handleAddValue(option.value)
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
        {isShowListbox && searchedList.length === 0 && (
          <ul className="list-box">
            <li className="no-list-msg">
              {values.length === items.length
                ? localization.text(
                    'select-no-option-left',
                    'Geen resterende opties'
                  )
                : localization.text(
                    'select-no-options-match-search',
                    'Geen enkele optie komt overeen met de zoekterm'
                  )}
            </li>
          </ul>
        )}
      </div>
      {!isEmpty(helperText) ? (
        <div className="select-helper-text">{helperText}</div>
      ) : null}
    </div>
  )
}
